import * as React from 'react';
import {Button} from 'primereact/button';
import {Tree, TreeProps} from 'primereact/tree';

import {PDATreeNode} from '../../helpers/tree';
import {ScrollContainer} from "../shared/ScrollContainer";
import {ProjectDoc} from '../../pages/projectDocs/models/ProjectDoc';
import {EmptyState} from "../synced/ui/EmptyState/EmptyState";


export interface TreeComponentProps extends TreeProps {
    treeNodes: PDATreeNode[];
    enableScroll?: boolean;
    disableToggle?: boolean;
    headerActions?: React.ReactNode;
}

export const TreeComponent: React.FC<TreeComponentProps> = (props) => {
    const {treeNodes, enableScroll, disableToggle, headerActions} = props;
    const [filterValue, setFilterValue] = React.useState('');
    const {
        treeNodes: _treeNodes,
        enableScroll: _enableScroll,
        disableToggle: _disableToggle,
        headerActions: _headerActions,
        onMenu: _onMenu,
        onPositionsChanged: _onPositionsChanged,
        leafMenuItems: _leafMenuItems,
        nodeMenuItems: _nodeMenuItems,
        workTypeMenuItems: _workTypeMenuItems,
        actMenuItems: _actMenuItems,
        actDocumentMenuItems: _actDocumentMenuItems,
        actDocumentSectionMenuItems: _actDocumentSectionMenuItems,
        actSectionMenuItems: _actSectionMenuItems,
        canEditNode: _canEditNode,
        projectDoc: _projectDoc,
        afterNodeValue: _afterNodeValue,
        criticalFolderMap: _criticalFolderMap,
        ...treeProps
    } = props as any;
    const [expandedKeys, setExpanded] = React.useState({});
    const [leafNodeIds, setLeafNodeIds] = React.useState<number[]>([]);
    const [isNotInitialToggle, setIsNotInitialToggle] = React.useState<boolean>(false);
    const onToggle = React.useCallback((e) => setExpanded(e.value), []);

    const onToggleNode = React.useCallback((node, expanded, leafs, isNotInitialToggleActual: boolean = isNotInitialToggle) => {
        if (node.children.length > 0) {
            if (!isNotInitialToggleActual || expandedKeys[node.key] === true) {
                if (node?.extended != false) {
                    expanded[node.key] = true;
                }
            }

            for (let child of node.children) {
                onToggleNode(child, expanded, leafs, isNotInitialToggleActual);
            }
        } else {
            if (isNotInitialToggleActual) {
                if (!leafNodeIds.some(x => x === node.key)) {
                    expanded[node.ParentId] = true;
                    leafs.push(node.key);
                }
            } else {
                leafs.push(node.key);
            }
        }
    }, [isNotInitialToggle, expandedKeys, leafNodeIds]);

    const onToggleAll = React.useCallback((isNotInitialToggleActual?: boolean) => {
        let expanded = {};
        const leafs: number[] = [];
        for (let node of treeNodes) {
            onToggleNode(node, expanded, leafs, isNotInitialToggleActual);
        }


        setExpanded(expanded);
        setLeafNodeIds([...leafNodeIds, ...leafs]);

        if (treeNodes.length > 0)
            setIsNotInitialToggle(true);
    }, [treeNodes, leafNodeIds, onToggleNode]);

    // eslint-disable-next-line
    React.useEffect(onToggleAll, [treeNodes]);


    const expandAll = React.useCallback(() => {
        setIsNotInitialToggle(false);
        onToggleAll(false);
    }, [onToggleAll]);

    const collapseAll = () => setExpanded({});


    const checkNodesExpanded = React.useCallback((nodes: PDATreeNode[], keys: object) =>
        nodes.every(x =>
            (keys[x.key ?? ""] !== undefined || x.children.length === 0) &&
            checkNodesExpanded(x.children, keys)
        ), []);

    const allExpanded: boolean = React.useMemo(() => checkNodesExpanded(treeNodes, expandedKeys), [treeNodes, expandedKeys, checkNodesExpanded]);

    const allCollapsed: boolean = React.useMemo(() => treeNodes.every(x => expandedKeys[x.key ?? ""] === undefined), [treeNodes, expandedKeys]);


    return (
        <React.Fragment>
            <div className="pda-tree-toolbar">
                {
                    !disableToggle &&
                    <React.Fragment>
                        <Button
                            icon="fa-solid fa-up-right-and-down-left-from-center"
                            className="pda-tree-toolbar-btn"
                            tooltip="Развернуть всё"
                            tooltipOptions={{position: 'top'}}
                            disabled={allExpanded}
                            onClick={expandAll}/>
                        <Button
                            icon="fa-solid fa-down-left-and-up-right-to-center"
                            className="pda-tree-toolbar-btn"
                            tooltip="Свернуть всё"
                            tooltipOptions={{position: 'top'}}
                            disabled={allCollapsed}
                            onClick={collapseAll}/>
                    </React.Fragment>
                }
                <div className="pda-tree-search">
                    <i className="fa-solid fa-magnifying-glass"/>
                    <input
                        type="text"
                        value={filterValue}
                        onChange={e => setFilterValue(e.target.value)}
                        placeholder="Поиск по структуре..."
                    />
                    {filterValue && (
                        <button type="button" onClick={() => setFilterValue('')}>
                            <i className="fa-solid fa-xmark"/>
                        </button>
                    )}
                </div>
                {headerActions && <div className="pda-tree-toolbar-actions">{headerActions}</div>}
            </div>
            {
                enableScroll &&
                <ScrollContainer useWrapper={false}>
                    {treeNodes.length > 0 ? <Tree
                        emptyMessage={'Не найдены элементы'}
                        filter
                        filterBy='label,Cypher'
                        filterValue={filterValue}
                        onFilterValueChange={e => setFilterValue(e.value)}
                        expandedKeys={expandedKeys}
                        onToggle={onToggle}
                        {...treeProps}
                        className={`pda-tree ${props.className ?? ``}`}
                        value={treeNodes}/> : <EmptyState icon="fa-folder-tree" title="Структура пуста"/>}

                </ScrollContainer>
            }
            {
                !enableScroll &&
                <Tree
                    filter
                    filterBy='label,Cypher'
                    filterValue={filterValue}
                    onFilterValueChange={e => setFilterValue(e.value)}
                    expandedKeys={expandedKeys}
                    onToggle={onToggle}
                    {...treeProps}
                    className={`pda-tree ${props.className ?? ``}`}
                    value={treeNodes}/>
            }
        </React.Fragment>
    );
}
